/**
 * Stored-citation open helper (PR-C1 serving endpoint → PR-C3 shared).
 *
 * The app authenticates by HEADER, not cookie, so a naked `<a href>` at our
 * tenant-scoped citation-file endpoint would 401. This fetches the PDF with
 * the app's OWN Authorization header and hands a new tab a blob URL. The tab
 * is opened SYNCHRONOUSLY, before any await, so the click's user-activation
 * covers it against popup blockers - call this DIRECTLY from the click
 * handler, never after an intervening await.
 *
 * ONE handler for every "open the stored citation" surface - the drawer's
 * "View the citation" CTA and the case block's "Citations on file" rows
 * (PR-C3 / CIT-13) - extracted from ViolationDetailDrawer so the two cannot
 * drift.
 *
 * Resolves `true` when the PDF reached a tab, `false` on failure (the
 * placeholder tab is closed and a console.warn records why). Never throws.
 */
import api from "@/api/base";

export async function openAuthedCitation(url: string): Promise<boolean> {
  if (!url) return false;
  // No "noopener": window.open returns NULL by spec when it is requested,
  // which would force the same-tab fallback. The blob is our own same-origin
  // content, so opener access is not a risk.
  const tab = window.open("", "_blank");
  try {
    const auth = api.defaults.headers.common.Authorization;
    const resp = await fetch(url, {
      headers:
        typeof auth === "string" && auth ? { Authorization: auth } : undefined,
    });
    if (!resp.ok) throw new Error(`citation fetch ${resp.status}`);
    const blob = await resp.blob();
    const objectUrl = URL.createObjectURL(blob);
    if (tab) {
      tab.location.href = objectUrl;
    } else {
      // Popup blocked despite the sync open - same-tab is better than nothing.
      window.location.href = objectUrl;
    }
    // Revoke well after the tab has loaded the blob.
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    return true;
  } catch (err) {
    tab?.close();
    // eslint-disable-next-line no-console
    console.warn("citation: stored copy could not be loaded", err);
    return false;
  }
}

/**
 * Authed SAVE-to-disk of any tenant-scoped binary endpoint: the case
 * citations-archive zip, and the portfolio export (`portfolioExport.ts`). Same
 * header-auth reason as `openAuthedCitation`, but these have no in-browser
 * viewer, so the blob goes to a synthetic `<a download>`. No `window.open`,
 * so no popup-blocker constraint: safe to call after an `await`. It is also
 * the one path `api/base.ts:149` cannot camelize, being `fetch`.
 *
 * `fallbackFilename` is used only when the response carries no
 * `Content-Disposition` filename (our endpoint always sets one). Resolves
 * `true` on success, `false` on failure (a console.warn records why); never
 * throws.
 */
export async function downloadAuthedFile(
  url: string,
  fallbackFilename: string,
): Promise<boolean> {
  if (!url) return false;
  try {
    const auth = api.defaults.headers.common.Authorization;
    const resp = await fetch(url, {
      headers:
        typeof auth === "string" && auth ? { Authorization: auth } : undefined,
    });
    if (!resp.ok) throw new Error(`fetch ${resp.status}`);
    const blob = await resp.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download =
      filenameFromDisposition(resp.headers.get("Content-Disposition")) ||
      fallbackFilename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    // Revoke after the browser has taken the blob (a sync revoke can race the
    // download in Safari).
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    return true;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`download failed: ${url}`, err);
    return false;
  }
}

/** `attachment; filename="citations_bf35.zip"` → `citations_bf35.zip`. */
export function filenameFromDisposition(header: string | null): string {
  if (!header) return "";
  // RFC 5987 `filename*=UTF-8''…` wins over the plain `filename=` when present.
  const extended = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (extended?.[1]) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      /* fall through to the plain form */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim() ?? "";
}

// ---------------------------------------------------------------------------
// Citation coverage - the ONE formula behind every "Download N of M" label and
// every explanation of the gap. The case block reads the server's tally; the
// same-charge cluster card tallies its own matters' `citationState`. Both call
// THESE functions, so a ticket can never be described one way on the card and
// another way in the case block.
// ---------------------------------------------------------------------------

export interface CitationCoverage {
  ticketCount: number;
  onFile: number;
  cityHasNone: number;
  fetchFailed: number;
  notFetched: number;
}

/** Tally a list of per-ticket states into the coverage shape. */
export function tallyCitationStates(
  states: readonly (string | undefined)[],
): CitationCoverage {
  const out: CitationCoverage = {
    ticketCount: states.length,
    onFile: 0,
    cityHasNone: 0,
    fetchFailed: 0,
    notFetched: 0,
  };
  states.forEach((s) => {
    if (s === "on_file") out.onFile += 1;
    else if (s === "city_has_none") out.cityHasNone += 1;
    else if (s === "fetch_failed") out.fetchFailed += 1;
    else out.notFetched += 1;
  });
  return out;
}

/**
 * The button label. The SHORTFALL still shows before the click: handing over
 * one file from a button that promised everything reads as our bug.
 *
 * Leo, 2026-09-28, on "Download all 1 citation": cut 50-70%. "Download" is
 * what the arrow already says and "all 1" is not English. 25 chars to 7.
 */
export function citationDownloadLabel(
  onFile: number,
  ticketCount: number,
): string {
  const noun = ticketCount === 1 ? "PDF" : "PDFs";
  return onFile === ticketCount
    ? `${ticketCount} ${noun} ↓`
    : `${onFile} of ${ticketCount} ${noun} ↓`;
}

/**
 * Why the rest are missing, in the customer's terms; empty when nothing is.
 * The city serving no image is terminal and nobody's fault; the other two are
 * ours, and the wording says so rather than implying the city came up short.
 */
export function citationGapReasons(
  coverage: CitationCoverage | undefined,
): string[] {
  if (!coverage) return [];
  return [
    coverage.cityHasNone > 0
      ? `${coverage.cityHasNone} unavailable from the city`
      : null,
    coverage.fetchFailed > 0
      ? `${coverage.fetchFailed} we could not retrieve`
      : null,
    coverage.notFetched > 0 ? `${coverage.notFetched} not requested yet` : null,
  ].filter((x): x is string => x !== null);
}
